import { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import localStorageConstants from "src/app/constants/localStorage.constants";
import type { WeeklyTemplate } from "src/app/models/screens/availabilityTimeslot.model";
import TimeSlot from "src/app/models/screens/availabilityTimeslot.model";
import {
  addRowToDateCustomizedSlot,
  addRowToWeeklyTemplate,
  addUserTag,
  deleteRowFromDateCustomSlots,
  deleteRowFromWeeklyTemplate,
  getCustomizedDateSlots,
  getParentChildren,
  getUserTags,
  getWeeklyTemplate,
  resetSlotsToWeeklyTemplate,
} from "src/app/utils/backendEndpointCalls.util";
import { timeStringToMinutes } from "src/app/utils/time.util";

type DateAvailabilityMap = {
  [isoDate: string]: TimeSlot[];
};

interface ParentChild {
  _id: string;
  firstName: string;
}

const AvailabilityCalendarScreenViewModel = () => {
  const [isSnackbarError, setIsSnackbarError] = useState<boolean>(true);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [isDisplayingSnackbar, setIsDisplayingSnackbar] =
    useState<boolean>(false);
  const navigate: NavigateFunction = useNavigate();
  const [role, setRole] = useState<string>("");
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState<boolean>(false);
  const [selectedAvailabilityOwnerId, setSelectedAvailabilityOwnerId] =
    useState<string>("you");
  const [userWeeklyTemplate, setUserWeeklyTemplate] = useState<WeeklyTemplate>({
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: [],
  });
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [dateOverrides, setDateOverrides] = useState<DateAvailabilityMap>({});
  const [activeTimeslots, setActiveTimeslots] = useState<TimeSlot[]>([]);
  const [isSelectedDateCustomized, setIsSelectedDateCustomized] =
    useState<boolean>(false);
  const [isRequestingNewTimeslot, setIsRequestingNewTimeslot] =
    useState<boolean>(false);
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [tags, setTags] = useState<Map<string, string>>(new Map());
  const [selectedTagKey, setSelectedTagKey] = useState<string | undefined>(
    undefined,
  );
  const [newTagText, setNewTagText] = useState<string>("");
  const [newTagColor, setNewTagColor] = useState<string>("#1976d2");
  const [meetingDescription, setMeetingDescription] = useState<string>("");
  const [isPropagateModalOpen, setIsPropagateModalOpen] =
    useState<boolean>(false);
  const [isPropagatingDeletion, setIsPropagatingDeletion] =
    useState<boolean>(false);
  const [isLoadingConfirmButton, setIsLoadingConfirmButton] =
    useState<boolean>(false);
  const [deletingStartTime, setDeletingStartTime] = useState<number>(-1);

  const getCurrentChildId = useCallback((): string | undefined => {
    return selectedAvailabilityOwnerId === "you"
      ? undefined
      : selectedAvailabilityOwnerId;
  }, [selectedAvailabilityOwnerId]);

  const getSelectedDayOfWeek = (): string => {
    if (!selectedDate) return "";
    return selectedDate.format("dddd");
  };

  const showError = useCallback((message: string) => {
    setIsDisplayingSnackbar(true);
    setIsSnackbarError(true);
    setSnackbarMessage(message);
  }, []);

  const getParentChildrenAsync = useCallback(async () => {
    setIsLoadingChildren(true);
    const response = await getParentChildren();
    setIsLoadingChildren(false);

    if (!response.success) {
      showError(response.error);
      return;
    }

    setChildren(response.children ?? []);
  }, [showError]);

  useEffect(() => {
    const storedRole = localStorage.getItem(localStorageConstants.ROLE) ?? "";
    setRole(storedRole);
  }, []);

  useEffect(() => {
    if (role !== "Parent") return;
    getParentChildrenAsync();
  }, [role, getParentChildrenAsync]);

  useEffect(() => {
    if (!isRequestingNewTimeslot) {
      setNewTagText("");
      setNewTagColor("#1976d2");
    }
  }, [isRequestingNewTimeslot]);

  const normalizeTagKey = (s: string) => s.trim();
  const isMeetingTagText = useCallback(
    (s: string) => normalizeTagKey(s).toLowerCase() === "meeting",
    [],
  );

  const isMeetingSelected = useMemo(() => {
    if (!selectedTagKey) return false;
    return isMeetingTagText(selectedTagKey);
  }, [selectedTagKey, isMeetingTagText]);

  const isMeetingTyped = useMemo(
    () => isMeetingTagText(newTagText),
    [newTagText, isMeetingTagText],
  );

  const getAllAvailabilityDate = useCallback(
    async (childId?: string) => {
      const weeklyTemplateResponse = await getWeeklyTemplate(childId);
      if (!weeklyTemplateResponse.success) {
        showError(weeklyTemplateResponse.error);
        return;
      }

      const customizedSlotsResponse = await getCustomizedDateSlots(childId);
      if (!customizedSlotsResponse.success) {
        showError(customizedSlotsResponse.error);
        return;
      }
      setUserWeeklyTemplate(weeklyTemplateResponse.weeklyTemplate);
      setDateOverrides(customizedSlotsResponse.customizedSlots);
    },
    [showError],
  );

  const getUserTagsAsync = useCallback(
    async (childId?: string) => {
      const getTagsResponse = await getUserTags(childId);
      if (!getTagsResponse.success) {
        showError(getTagsResponse.error);
        return;
      }

      const nextTags = new Map<string, string>();
      Object.keys(getTagsResponse.tags ?? {}).forEach((key: string) => {
        nextTags.set(key, getTagsResponse.tags[key]);
      });
      setTags(nextTags);
    },
    [showError],
  );

  useEffect(() => {
    const childId = getCurrentChildId();
    getAllAvailabilityDate(childId);
    getUserTagsAsync(childId);
  }, [
    selectedAvailabilityOwnerId,
    getAllAvailabilityDate,
    getCurrentChildId,
    getUserTagsAsync,
  ]);

  useEffect(() => {
    if (!selectedDate) return;
    const iso = selectedDate.format("YYYY-MM-DD");
    const weekday: string = selectedDate.format("dddd");
    if (dateOverrides[iso]) {
      setIsSelectedDateCustomized(true);
      setActiveTimeslots(dateOverrides[iso]);
    } else {
      setIsSelectedDateCustomized(false);
      setActiveTimeslots(userWeeklyTemplate[weekday]);
    }
  }, [selectedDate, dateOverrides, userWeeklyTemplate]);

  useEffect(() => {
    if (activeTimeslots.length <= 1) return;
    const isSorted = activeTimeslots.every(
      (slot, index) =>
        index === 0 || activeTimeslots[index - 1].from <= slot.from,
    );
    if (isSorted) return;
    const sorted = [...activeTimeslots].sort((a, b) => a.from - b.from);
    setActiveTimeslots(sorted);
  }, [activeTimeslots]);

  const onSelectedAvailabilityOwnerChanged = (ownerId: string) => {
    setSelectedAvailabilityOwnerId(ownerId);
    setSelectedDate(null);
    setActiveTimeslots([]);
    setIsSelectedDateCustomized(false);
    setIsRequestingNewTimeslot(false);
    setSelectedTagKey(undefined);
    setNewTagText("");
    setNewTagColor("#1976d2");
    setMeetingDescription("");
  };

  const onDateSelected = (date: Dayjs | null) => {
    setSelectedDate(date);
    setIsRequestingNewTimeslot(false);
  };

  const onRemoveTimeslot = async (from: number) => {
    if (!selectedDate) return;
    const deleteRowResponse = await deleteRowFromDateCustomSlots(
      selectedDate.format("YYYY-MM-DD"),
      from,
      getCurrentChildId(),
    );
    if (!deleteRowResponse.success) {
      setSnackbarMessage(deleteRowResponse.error);
      setIsSnackbarError(true);
      setIsDisplayingSnackbar(true);
      return;
    }
    let slotExistsInTemplate: boolean =
      dateOverrides[selectedDate.format("YYYY-MM-DD")] === undefined;
    if (!slotExistsInTemplate) {
      const timeslot = dateOverrides[selectedDate.format("YYYY-MM-DD")]?.find(
        (slot: TimeSlot) => slot.from.toString() === from.toString(),
      );
      slotExistsInTemplate = userWeeklyTemplate[getSelectedDayOfWeek()].some(
        (value: TimeSlot) =>
          value.from === timeslot?.from &&
          value.to === timeslot?.to &&
          value.tag === timeslot?.tag &&
          value.description === timeslot?.description,
      );
    }
    await getAllAvailabilityDate(getCurrentChildId());
    if (!slotExistsInTemplate) {
      closeModal();
      return;
    }
    setDeletingStartTime(from);
    setIsPropagatingDeletion(true);
    setIsPropagateModalOpen(true);
  };

  const onSubmitPressed = async () => {
    let isOverlapping: boolean = false;
    activeTimeslots.forEach((timeslot: TimeSlot) => {
      if (
        !(
          timeStringToMinutes(endTime) <= timeslot.from ||
          timeStringToMinutes(startTime) >= timeslot.to
        )
      )
        isOverlapping = true;
    });
    if (isOverlapping) {
      setIsSnackbarError(true);
      setIsDisplayingSnackbar(true);
      setSnackbarMessage(
        "This timeslot overlaps with previously added timeslots for this day",
      );
      return;
    }
    const addCustomizedSlotResponse = await addRowToDateCustomizedSlot(
      selectedDate?.format("YYYY-MM-DD")!,
      timeStringToMinutes(startTime),
      timeStringToMinutes(endTime),
      selectedTagKey!,
      meetingDescription,
      getCurrentChildId(),
    );
    if (!addCustomizedSlotResponse.success) {
      setIsSnackbarError(true);
      setIsDisplayingSnackbar(true);
      setSnackbarMessage(addCustomizedSlotResponse.error);
      return;
    }
    setIsRequestingNewTimeslot(false);
    await getAllAvailabilityDate(getCurrentChildId());
    isOverlapping = false;
    userWeeklyTemplate[getSelectedDayOfWeek()].forEach((timeslot: TimeSlot) => {
      if (
        !(
          timeStringToMinutes(endTime) <= timeslot.from ||
          timeStringToMinutes(startTime) >= timeslot.to
        )
      )
        isOverlapping = true;
    });
    if (isOverlapping) {
      closeModal();
      return;
    }
    setIsPropagatingDeletion(false);
    setIsPropagateModalOpen(true);
  };

  const onResetToWeeklyTemplate = async () => {
    if (!selectedDate) return;
    const resetToTemplateResponse = await resetSlotsToWeeklyTemplate(
      selectedDate.format("YYYY-MM-DD"),
      getCurrentChildId(),
    );
    if (!resetToTemplateResponse.success) {
      setSnackbarMessage(resetToTemplateResponse.error);
      setIsSnackbarError(true);
      setIsDisplayingSnackbar(true);
      return;
    }
    await getAllAvailabilityDate(getCurrentChildId());
  };

  const onNavigateBack = () => {
    navigate("/availabilityManagement");
  };

  const requestNewTimeslot = () => {
    setIsRequestingNewTimeslot(true);
  };

  const toggleTag = (tagKey: string) => {
    setSelectedTagKey(tagKey);
  };

  const onCreateTag = async () => {
    const normalized = normalizeTagKey(newTagText);
    if (!normalized) return;
    const exists = Array.from(tags.keys()).some(
      (t: string) =>
        normalizeTagKey(t.toString()).toLowerCase() ===
        normalized.toLowerCase(),
    );
    if (exists) {
      setIsDisplayingSnackbar(true);
      setIsSnackbarError(true);
      setSnackbarMessage("Tag already exists");
      return;
    }

    const addTagResponse = await addUserTag(
      newTagText,
      newTagColor,
      getCurrentChildId(),
    );
    if (!addTagResponse.success) {
      setIsDisplayingSnackbar(true);
      setIsSnackbarError(true);
      setSnackbarMessage(addTagResponse.error);
      return;
    }

    const nextTags = new Map<string, string>();
    Object.keys(addTagResponse.tags ?? {}).forEach((key: string) => {
      nextTags.set(key, addTagResponse.tags[key]);
    });
    setTags(nextTags);
    setNewTagText("");
  };

  const closeModal = () => {
    setIsPropagateModalOpen(false);
    setSelectedTagKey(undefined);
    setNewTagText("");
    setNewTagColor("#1976d2");
    setMeetingDescription("");
  };

  const confirmPropagateAction = async () => {
    setIsLoadingConfirmButton(true);
    if (isPropagatingDeletion) {
      const deleteTimeslotFromWeeklyTemplateResponse =
        await deleteRowFromWeeklyTemplate(
          getSelectedDayOfWeek(),
          deletingStartTime,
          getCurrentChildId(),
        );
      if (!deleteTimeslotFromWeeklyTemplateResponse.success) {
        setIsDisplayingSnackbar(true);
        setIsSnackbarError(true);
        setSnackbarMessage(deleteTimeslotFromWeeklyTemplateResponse.error);
        setIsLoadingConfirmButton(false);
        return;
      }
    } else {
      const addTimeslotToWeeklyTemplateResponse = await addRowToWeeklyTemplate(
        getSelectedDayOfWeek(),
        timeStringToMinutes(startTime),
        timeStringToMinutes(endTime),
        selectedTagKey!,
        meetingDescription,
        getCurrentChildId(),
      );
      if (!addTimeslotToWeeklyTemplateResponse.success) {
        setIsDisplayingSnackbar(true);
        setIsSnackbarError(true);
        setSnackbarMessage(addTimeslotToWeeklyTemplateResponse.error);
        setIsLoadingConfirmButton(false);
        return;
      }
    }

    await getAllAvailabilityDate(getCurrentChildId());
    setIsLoadingConfirmButton(false);
    closeModal();
  };

  return {
    selectedDate,
    activeTimeslots,
    onDateSelected,
    onRemoveTimeslot,
    onResetToWeeklyTemplate,
    onNavigateBack,
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    isSelectedDateCustomized,
    isRequestingNewTimeslot,
    requestNewTimeslot,
    startTime,
    endTime,
    setStartTime,
    setEndTime,
    onSubmitPressed,
    tags,
    selectedTagKey,
    normalizeTagKey,
    toggleTag,
    newTagText,
    setNewTagText,
    newTagColor,
    setNewTagColor,
    onCreateTag,
    isMeetingSelected,
    isMeetingTyped,
    meetingDescription,
    setMeetingDescription,
    isPropagateModalOpen,
    closeModal,
    confirmPropagateAction,
    getSelectedDayOfWeek,
    isPropagatingDeletion,
    isLoadingConfirmButton,
    role,
    children,
    isLoadingChildren,
    selectedAvailabilityOwnerId,
    onSelectedAvailabilityOwnerChanged,
  };
};

export default AvailabilityCalendarScreenViewModel;
